import React, { useEffect, useState } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  InputLabel,
  Select,
  OutlinedInput,
  Box,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import IOSSwitch from "../../../Components/Switch/IOSSwitch";
import { MultiSelect } from "react-multi-select-component";
import configServ from "../../../services/config";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

const CouponsForm = ({
  isCreate,
  setIsCreate,
  onSuccess,
  editData,
  isEdit,
  setEditCouponData,
  setIsEdit,
}) => {
  const { company_id, user_id } = useSelector((state) => state.GlobalVariables);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    coupon_name: "",
    code: "",
    discount_type: "percentage",
    discount_value: "",
    max_discount: "",
    min_order_amount: "",
    valid_from: null,
    valid_to: null,
    usage_limit: "",
    is_active: true,
    apply_on: "all",
    applicable_items: [],
  });

  useEffect(() => {
    if (isEdit && editData) {
      setFormData({
        coupon_name: editData.coupon_name || "",
        code: editData.code || "",
        discount_type: editData.discount_type || "percentage",
        discount_value: editData.discount_value || "",
        max_discount: editData.max_discount || "",
        min_order_amount: editData.min_order_amount || "",
        valid_from: editData.valid_from || null,
        valid_to: editData.valid_to || null,
        usage_limit: editData.usage_limit || "",
        is_active: Boolean(editData.is_active),
        apply_on: editData.apply_on || "all",
        applicable_items: editData.items?.map((item) => item.product_id) || [],
      });
    }
  }, [isEdit, editData]);

  const fetchItemList = async () => {
    try {
      const dataToSend = {
        company_id,
        search_by: null,
        search: null,
        per_page: null,
        page: null,
      };
      const res = await configServ.getItemList(dataToSend);
      if (res.status === 200 && res.data.length > 0) {
        setItems(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (isCreate) {
      fetchItemList();
    }
  }, [isCreate]);

  // Convert product list to label/value format
  const productOptions = items.map((item) => ({
    label: item.item_name,
    value: item.id,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitch = (e) => {
    setFormData((prev) => ({
      ...prev,
      is_active: e.target.checked,
    }));
  };

  const handleDateChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value ? dayjs(value).format("YYYY-MM-DD") : null, // save as string for API
    }));
  };

  const handleGenerateCode = () => {
    const random = Math.random().toString(36).substr(2, 6).toUpperCase();
    setFormData((prev) => ({ ...prev, code: `SAVE${random}` }));
  };

  const handleReset = () => {
    setFormData({
      coupon_name: "",
      code: "",
      discount_type: "percentage",
      discount_value: "",
      max_discount: "",
      min_order_amount: "",
      valid_from: null,
      valid_to: null,
      usage_limit: "",
      is_active: true,
      apply_on: "all",
      applicable_items: [],
    });
  };
  const handleClose = () => {
    setIsCreate(false);
    setEditCouponData(null);
    setIsEdit(false);
  };

  const validateForm = () => {
    if (!formData.coupon_name.trim()) {
      Swal.fire({ icon: "error", title: "Coupon Name is required" });
      return false;
    }
  
    if (!formData.code.trim()) {
      Swal.fire({ icon: "error", title: "Code is required" });
      return false;
    }
  
    if (!formData.discount_value || isNaN(formData.discount_value)) {
      Swal.fire({ icon: "error", title: "Valid Discount Value is required" });
      return false;
    }
  
    if (formData.max_discount && isNaN(formData.max_discount)) {
      Swal.fire({ icon: "error", title: "Max Discount must be a number" });
      return false;
    }
  
    if (formData.min_order_amount && isNaN(formData.min_order_amount)) {
      Swal.fire({ icon: "error", title: "Min Order Amount must be a number" });
      return false;
    }
  
    if (!formData.valid_from) {
      Swal.fire({ icon: "error", title: "Valid From date is required" });
      return false;
    }
  
    if (!formData.valid_to) {
      Swal.fire({ icon: "error", title: "Valid To date is required" });
      return false;
    }
  
    if (
      dayjs(formData.valid_to).isBefore(dayjs(formData.valid_from), "day")
    ) {
      Swal.fire({ icon: "error", title: "Valid To must be after Valid From" });
      return false;
    }
  
    if (formData.usage_limit && isNaN(formData.usage_limit)) {
      Swal.fire({ icon: "error", title: "Usage Limit must be a number" });
      return false;
    }
  
    if (
      formData.apply_on === "specific_items" &&
      (!formData.applicable_items || formData.applicable_items.length === 0)
    ) {
      Swal.fire({ icon: "error", title: "Please select at least one applicable item" });
      return false;
    }
  
    return true;
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const payload = {
      company_id,
      ...formData,
      valid_from: formData.valid_from
        ? dayjs(formData.valid_from).format("YYYY-MM-DD")
        : null,
      valid_to: formData.valid_to
        ? dayjs(formData.valid_to).format("YYYY-MM-DD")
        : null,
    };

    // Add creator/updater
    if (isEdit) {
      payload.updated_by = user_id;
      payload.coupon_id = editData.id; // Make sure this is set in formData during edit
    } else {
      payload.created_by = user_id;
    }

    try {
      const res = isEdit
        ? await configServ.updateCoupon(payload)
        : await configServ.storeCoupon(payload);

      if (res.status) {
        Swal.fire({
          icon: "success",
          title: isEdit ? "Coupon Updated" : "Coupon Created",
          text: `The coupon was successfully ${
            isEdit ? "updated" : "created"
          }.`,
        });

        handleReset();
        onSuccess();
        setIsCreate(false); // or toggle based on UI logic
      } else {
        Swal.fire({
          icon: "error",
          title: "Operation Failed",
          text: res.data?.message || "Something went wrong!",
        });
      }
    } catch (err) {
      console.error("Coupon save error:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Server error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        style={{
          width: "100%",
          backgroundColor: "#f5fafe",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.08)",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <TextField
                label="Coupon Name"
                name="coupon_name"
                fullWidth
                value={formData.coupon_name}
                onChange={handleChange}
                size="small"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Coupon Code"
                name="code"
                fullWidth
                value={formData.code}
                onChange={handleChange}
                size="small"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGenerateCode}
                sx={{ height: "100%" }}
              >
                Generate Code
              </Button>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Discount Type"
                name="discount_type"
                select
                fullWidth
                value={formData.discount_type}
                onChange={handleChange}
                size="small"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed">Fixed</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Discount Value"
                name="discount_value"
                fullWidth
                type="number"
                value={formData.discount_value}
                onChange={handleChange}
                size="small"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              />
            </Grid>

            {formData.discount_type === "percentage" && (
              <Grid item xs={12} md={3}>
                <TextField
                  label="Max Discount"
                  name="max_discount"
                  fullWidth
                  type="number"
                  value={formData.max_discount}
                  onChange={handleChange}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 1,
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={3}>
              <TextField
                label="Minimum Order Amount"
                name="min_order_amount"
                fullWidth
                type="number"
                value={formData.min_order_amount}
                onChange={handleChange}
                size="small"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <DatePicker
                label="Valid From"
                value={formData.valid_from ? dayjs(formData.valid_from) : null}
                onChange={(newValue) =>
                  handleDateChange("valid_from", newValue)
                }
                renderInput={(params) => <TextField fullWidth {...params} />}
                slotProps={{ textField: { size: "small" } }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <DatePicker
                label="Valid To"
                value={formData.valid_to ? dayjs(formData.valid_to) : null}
                onChange={(newValue) => handleDateChange("valid_to", newValue)}
                renderInput={(params) => <TextField fullWidth {...params} />}
                slotProps={{ textField: { size: "small" } }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Usage Limit"
                name="usage_limit"
                fullWidth
                type="number"
                value={formData.usage_limit}
                onChange={handleChange}
                size="small"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Apply On"
                name="apply_on"
                select
                fullWidth
                value={formData.apply_on}
                onChange={handleChange}
                size="small"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              >
                <MenuItem value="all">All Items</MenuItem>
                <MenuItem value="specific_items">Specific Items</MenuItem>
              </TextField>
            </Grid>

            {formData.apply_on === "specific_items" && (
              <Grid item xs={12} md={3}>
                {/* <InputLabel sx={{ mb: 1 }}>Applicable Items</InputLabel> */}
                <MultiSelect
                  options={productOptions}
                  value={productOptions.filter((opt) =>
                    formData.applicable_items.includes(opt.value)
                  )}
                  onChange={(selected) =>
                    setFormData((prev) => ({
                      ...prev,
                      applicable_items: selected.map((opt) => opt.value),
                    }))
                  }
                  labelledBy="Select"
                  hasSelectAll={false}
                  overrideStrings={{
                    selectSomeItems: "Select Applicable Items...",
                    allItemsAreSelected: "All selected",
                    selectAll: "Select All",
                    search: "Search...",
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={formData.is_active}
                    onChange={handleSwitch}
                    sx={{ m: 1 }}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (isEdit) {
                      handleClose(); // close modal or form
                    } else {
                      handleReset(); // reset form fields
                    }
                  }}
                >
                  {isEdit ? "Close" : "Reset"}
                </Button>

                <Button type="submit" variant="contained" disabled={loading}>
                  {loading
                    ? isEdit
                      ? "Updating..."
                      : "Saving..."
                    : isEdit
                    ? "Update Coupon"
                    : "Save Coupon"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </motion.div>
    </LocalizationProvider>
  );
};

export default CouponsForm;
